import React from 'react';
import { ExportV2LimitedLineup } from '../../../../../../../../shared/types/exportV2';
import {
  addDaysToDateUTC,
  dateToIsoUTC,
  getLocaleDateStringFromIso,
  isoToDateUTC,
} from '../../../../../../../../shared/lib/time';
import {
  DailyProgramEvent,
  DailyProgramVenue,
  DailyProgramVenueMealPeriod,
} from '../../../../../../../../shared/types/dailyProgram';
import { useData } from '../../context/DataContext';

export interface SimilarTimes {
  date: string;
  times: string[];
  mealPeriods: DailyProgramVenueMealPeriod[];
}

interface SimilarTimesHocChildProps {
  similarTimes: SimilarTimes[];
}

interface SimilarTimesHocPropsBase {}

interface SimilarTimesHocPropsEvent {
  type: 'events';
  item: DailyProgramEvent;
}

interface SimilarTimesHocPropsVenue {
  type: 'venues';
  item: DailyProgramVenue;
}

export type SimilarTimesHocPropsBasic = SimilarTimesHocPropsBase &
  (SimilarTimesHocPropsEvent | SimilarTimesHocPropsVenue);

type SimilarTimesHocProps = SimilarTimesHocPropsBasic & {
  children: (props: SimilarTimesHocChildProps) => React.ReactNode;
};

export default function SimilarTimesHoc(props: SimilarTimesHocProps) {
  const { type, item, children } = props;
  const { todayLineup, data } = useData();

  const similarTimes: SimilarTimes[] = [];

  function extractTimes(lineup: ExportV2LimitedLineup) {
    return type === 'events'
      ? lineup.events.filter(e => e.templateId === item.templateId).map(e => e.timeString)
      : lineup.venues.find(v => v.templateId === item.templateId)?.timeStrings || [];
  }

  function extractMealPeriods(lineup: ExportV2LimitedLineup) {
    return lineup.venues.find(v => v.templateId === item.templateId)?.mealPeriodStrings || [];
  }

  const todayTimes = extractTimes(todayLineup);
  const todayMealPeriods = extractMealPeriods(todayLineup);

  if (todayTimes.length > 0) {
    similarTimes.push({
      date: data.appConfig.TODAY_LABEL,
      times: todayTimes,
      mealPeriods: todayMealPeriods,
    });
  }

  let workingDay = isoToDateUTC(todayLineup.date);

  workingDay = addDaysToDateUTC(workingDay, 1);
  const tomorrowLineup = data.lineup[dateToIsoUTC(workingDay)];

  if (tomorrowLineup) {
    const tomorrowTimes = extractTimes(tomorrowLineup);
    const tomorrowMealPeriods = extractMealPeriods(tomorrowLineup);

    if (tomorrowTimes.length > 0) {
      similarTimes.push({
        date: data.appConfig.TOMORROW_LABEL,
        times: tomorrowTimes,
        mealPeriods: tomorrowMealPeriods,
      });
    }
  }

  workingDay = addDaysToDateUTC(workingDay, 1);
  let nextLineup = data.lineup[dateToIsoUTC(workingDay)];

  while (nextLineup) {
    const nextTimes = extractTimes(nextLineup);
    const nextMealPeriods = extractMealPeriods(nextLineup);

    if (nextTimes.length > 0) {
      similarTimes.push({
        date: getLocaleDateStringFromIso(nextLineup.date, {
          month: 'short',
          day: 'numeric',
          weekday: 'short',
        }),
        times: nextTimes,
        mealPeriods: nextMealPeriods,
      });
    }

    workingDay = addDaysToDateUTC(workingDay, 1);
    nextLineup = data.lineup[dateToIsoUTC(workingDay)];
  }

  const childProps: SimilarTimesHocChildProps = {
    similarTimes,
  };

  return <>{children(childProps)}</>;
}
